import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import { MediaRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/MediaRel';
import fr from 'facet-rest';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { BodyTextMedium, HeadlineTextLarge, TitleTextMedium } from 'Packages/FacetDesign/Components';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import {
  FDBodyText, FDHeadlineText, FDStack, FDTitleText,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('title', {
    _component: () => HeadlineTextLarge,
  }, overrideReadOnly)
  .override({ name: 'title', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: { Component: FDHeadlineText, size: 'lg' },
  }, overrideReadOnly)
  .nameOverride('subtitle', {
    _component: () => TitleTextMedium,
  }, overrideReadOnly)
  .override({ name: 'subtitle', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: { Component: FDTitleText, size: 'md' },
  }, overrideReadOnly)
  .nameOverride('description', {
    _component: () => BodyTextMedium,
  }, overrideReadOnly)
  .override({ name: 'description', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: { Component: FDBodyText, size: 'md' },
  }, overrideReadOnly);

const useStyles = buildUseStyles('StructuredContentSplit', () => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '16px',
    justifyContent: 'space-between',
    '& [data-element-semantic="structured-content-split-text"]': {
      flexBasis: '300px',
      flex: 1,
    },
    '& [data-element-semantic="media-rel-video-container"]': {
      flexBasis: '300px',
      flex: 1,
    },
  },
  rootFlipped: {
    flexDirection: 'row-reverse',
  },
}));

export const Split = ({ structuredContentResource, flipped }) => {
  const classes = useStyles();
  return (
    <div className={classNames(classes.root, {
      [classes.rootFlipped]: flipped,
    })}
    >
      <FDStack
        direction={'column'}
        flex={1}
        {...dataAttrs('structured-content-split-text', structuredContentResource)}
      >
        <MetaControl
          resource={structuredContentResource}
          attrName={'title'}
          metaOverrideSet={metaOverrideSet}
          CustomProps={{
            ...dataAttrs('structured-content-split-title', structuredContentResource),
          }}
        />
        <MetaControl
          resource={structuredContentResource}
          attrName={'subtitle'}
          metaOverrideSet={metaOverrideSet}
          CustomProps={{
            ...dataAttrs('structured-content-split-subtitle', structuredContentResource),
          }}
        />
        <MetaControl
          resource={structuredContentResource}
          attrName={'description'}
          metaOverrideSet={metaOverrideSet}
          CustomProps={{
            ...dataAttrs('structured-content-split-description', structuredContentResource),
          }}
        />
      </FDStack>
      <MediaRel
        relationship={structuredContentResource.relationship(fr.relationships.media.name)}
        flex={1}
      />
    </div>
  );
};

Split.defaultProps = {
  flipped: false,
};

Split.propTypes = {
  structuredContentResource: PropTypes.object.isRequired,
  flipped: PropTypes.bool,
};
